<template>
  <div id="kye-date3-table">
    <table
      cellspacing="0"
      cellpadding="0"
      class="kye-date3-table el-date-table"
      @click="handleClick"
      @mousemove="handleMouseMove"
      @mouseleave="handleTableMouseLeave"
      :class="{
        'is-week-mode': selectionMode === 'week',
        'picker2-week': isWeekRange,
        'single-week': !isWeekRange && selectionMode === 'week',
        'is-lunar-mode': showLunar,
        'kye-date3-table-dates': selectionMode === 'dates'
      }">
      <tbody>
        <tr>
          <th v-if="showWeekNumber">周</th>
          <th v-for="(week, key) in WEEKS" :key="key">{{ week }}</th>
        </tr>
        <tr
          class="el-date-table__row"
          v-for="(row, key) in rows"
          :class="{ current: isWeekActive(showWeekNumber ? row[2] : row[1]) }"
          :key="key">
          <td v-for="(cell, key) in row" :class="getCellClasses(cell)" :key="key">
            <div
              @mouseenter="event => handleMouseEnter(event, cell)"
              @mouseleave="event => handleMouseLeave(event, cell)">
              <span v-if="cell.isTodayWeek">
                <li>本周</li>
              </span>
              <span v-else>
                {{ cell.text }}
                <em v-if="showLunar && cell.lunar" class="el-date-table__row_launar">{{ cell.lunar }}</em>
              </span>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <PopoverDate :content="popperContent"></PopoverDate>
  </div>
</template>

<script>
import moment from 'moment'
import {
  getFirstDayOfMonth,
  getDayCountOfMonth,
  getWeekNumber,
  getStartDateOfMonth,
  prevDate,
  nextDate,
  isDate
} from '../util'
import { hasClass } from 'element-ui/lib/utils/dom'
import { arrayFindIndex, arrayFind, coerceTruthyValueToArray } from 'element-ui/lib/utils/util'
import { PopupManager } from 'element-ui/lib/utils/popup'
import PopoverDate from './popover'
import { getLunarText } from '@/public/utils/lunar/index.js'
import '../style/date-table.scss'

const WEEKS = ['日', '一', '二', '三', '四', '五', '六']
const clearHours = function (time) {
  const cloneDate = new Date(time)
  cloneDate.setHours(0, 0, 0, 0)
  return cloneDate.getTime()
}

// remove the first element that satisfies `pred` from arr
// return a new array if modification occurs
// return the original array otherwise
const removeFromArray = function (arr, pred) {
  const idx = typeof pred === 'function' ? arrayFindIndex(arr, pred) : arr.indexOf(pred)
  return idx >= 0 ? [...arr.slice(0, idx), ...arr.slice(idx + 1)] : arr
}

let LunarTextMap = {}

export default {
  components: {
    PopoverDate
  },

  props: {
    showLunar: {
      type: Boolean,
      default: true
    },

    firstDayOfWeek: {
      default: 7,
      type: Number,
      validator: val => val >= 1 && val <= 7
    },

    value: {},

    defaultValue: {
      validator(val) {
        // either: null, valid Date object, Array of valid Date objects
        return val === null || isDate(val) || (Array.isArray(val) && val.every(isDate))
      }
    },

    date: {},

    selectionMode: {
      default: 'day'
    },

    showWeekNumber: {
      type: Boolean,
      default: false
    },

    disabledDate: {},

    cellClassName: {},

    activeInputIndex: Number,

    showTime: Boolean,

    minDate: {},

    maxDate: {},

    rangeState: {
      default() {
        return {
          startDate: null,
          endDate: null,
          selecting: false,
          isPick: false,
          row: null,
          column: null
        }
      }
    },
    cellPopperContent: [String, Function],
    isWeekRange: {
      type: Boolean,
      default: false
    },
    singleValue: Boolean
  },

  computed: {
    offsetDay() {
      const week = this.firstDayOfWeek
      // 周日为界限，左右偏移的天数，3217654 例如周一就是 -1，目的是调整前两行日期的位置
      return week > 3 ? 7 - week : -week
    },

    WEEKS() {
      const week = this.firstDayOfWeek
      return WEEKS.concat(WEEKS).slice(week, week + 7)
    },

    year() {
      return this.date.getFullYear()
    },

    month() {
      return this.date.getMonth()
    },

    startDate() {
      return getStartDateOfMonth(this.year, this.month)
    },

    rows() {
      // TODO: refactory rows / getCellClasses
      const date = new Date(this.year, this.month, 1)
      let day = getFirstDayOfMonth(date) // day of first day
      const dateCountOfMonth = getDayCountOfMonth(date.getFullYear(), date.getMonth())
      const dateCountOfLastMonth = getDayCountOfMonth(
        date.getFullYear(),
        date.getMonth() === 0 ? 11 : date.getMonth() - 1
      )

      day = day === 0 ? 7 : day

      const offset = this.offsetDay
      const rows = this.tableRows
      let count = 1
      let firstDayPosition

      const startDate = this.startDate
      const disabledDate = this.disabledDate
      const activeInputIndex = this.activeInputIndex
      const cellClassName = this.cellClassName
      const selectedDate = this.selectionMode === 'dates' ? coerceTruthyValueToArray(this.value) : []
      const now = clearHours(new Date())

      for (let i = 0; i < 6; i++) {
        const row = rows[i]

        if (this.showWeekNumber) {
          // if (!row[0]) {
          const num = this.firstDayOfWeek !== 7 ? 1 : 0
          row[0] = { type: 'week', text: getWeekNumber(nextDate(startDate, i * 7 + num)) + '周' }
          // }
        }

        for (let j = 0; j < 7; j++) {
          let cell = row[this.showWeekNumber ? j + 1 : j]
          if (!cell) {
            cell = { row: i, column: j, type: 'normal', inRange: false, start: false, end: false }
          }

          cell.type = 'normal'

          const index = i * 7 + j
          const time = nextDate(startDate, index - offset).getTime()
          if (this.$props.singleValue) {
            // 单边时 没有 minDate的时候需要去掉，防止只存在max的时候会选中所有前面的。
            cell.inRange = this.minDate && time >= clearHours(this.minDate) && time <= clearHours(this.maxDate)
          } else {
            cell.inRange = time >= clearHours(this.minDate) && time <= clearHours(this.maxDate)
          }
          cell.start = this.minDate && time === clearHours(this.minDate)
          cell.end = this.maxDate && time === clearHours(this.maxDate)
          const isToday = time === now

          if (isToday) {
            cell.type = 'today'
            if (this.showWeekNumber) {
              row[0].isTodayWeek = true
            }
          }

          if (i >= 0 && i <= 1) {
            if (j + i * 7 >= day + offset) {
              cell.text = count++
              if (count === 2) {
                firstDayPosition = i * 7 + j
              }
            } else {
              cell.text = dateCountOfLastMonth - (day + offset - (j % 7)) + 1 + i * 7
              cell.type = 'prev-month'
            }
          } else {
            if (count <= dateCountOfMonth) {
              cell.text = count++
              if (count === 2) {
                firstDayPosition = i * 7 + j
              }
            } else {
              cell.text = count++ - dateCountOfMonth
              cell.type = 'next-month'
            }
          }

          let cellDate = new Date(time)
          let disabled = typeof disabledDate === 'function' && disabledDate(cellDate)

          // 结束时间不允许大于开始时间
          if (this.selectionMode === 'range' && !disabled && activeInputIndex === 1) {
            if (!this.singleValue && this.minDate) {
              disabled = cellDate.getTime() < moment(this.minDate).startOf('day').unix() * 1000
            }
            // 单边值情况下，如果只有只有一个值情况下，minDate maxDate 都为空
            if (this.singleValue && this.value[0]) {
              disabled = cellDate.getTime() < moment(this.value[0]).startOf('day').unix() * 1000
            }
          }

          // 开始时间不允许大于结束时间
          if (this.selectionMode === 'range' && !disabled && activeInputIndex === 0) {
            if (!this.singleValue && this.maxDate && !this.minDate) {
              disabled = cellDate.getTime() > moment(this.maxDate).endOf('day').unix() * 1000
            }
            // 单边值情况下，如果只有只有一个值情况下，minDate maxDate 都为空
            if (this.singleValue && this.value[1] && !this.value[0]) {
              disabled = cellDate.getTime() > moment(this.value[1]).startOf('day').unix() * 1000
            }
          }

          cell.disabled = disabled
          cell.selected = arrayFind(selectedDate, date => {
            if (this.selectionMode === 'dates') {
              return moment(date).isSame(moment(cellDate), 'day')
            }
            return date.getTime() === cellDate.getTime()
          })
          // 添加农历信息
          if (this.showLunar) {
            // 这里有性能问题。转换之后先存入本地。下次有的时候直接使用内存中的值
            cell.lunar = LunarTextMap[cellDate] || getLunarText(cellDate)
            if (!LunarTextMap[cellDate]) {
              LunarTextMap[cellDate] = cell.lunar
            }
          }

          cell.customClass = typeof cellClassName === 'function' && cellClassName(cellDate)

          this.$set(row, this.showWeekNumber ? j + 1 : j, cell)
        }

        // isRowCheckWeekActive主要给范围周做处理的
        if (this.selectionMode === 'week' && !this.isWeekRange) {
          const start = this.showWeekNumber ? 1 : 0
          const end = this.showWeekNumber ? 7 : 6
          const isWeekActive = this.isWeekActive(row[start + 1])
          row[start].inRange = isWeekActive
          row[start].start = isWeekActive
          row[end].inRange = isWeekActive
          row[end].end = isWeekActive
        }
      }

      rows.firstDayPosition = firstDayPosition

      return rows
    }
  },

  watch: {
    'rangeState.hoverDate'(newVal) {
      this.markRange()
    },

    'rangeState.endDate'(newVal) {
      this.markRange(newVal)
    },

    minDate(newVal, oldVal) {
      if (this.showTime) {
        this.markRange(newVal)
        return
      }
      if ((newVal && !oldVal)) {
        this.rangeState.selecting = true
        this.markRange(newVal)
      } else if (!newVal) {
        this.rangeState.selecting = false
        this.markRange(newVal)
      } else {
        this.markRange()
      }
    },

    maxDate(newVal, oldVal) {
      if ((newVal && !oldVal) || (this.showTime && newVal !== oldVal)) {
        this.rangeState.selecting = false
        this.markRange(newVal)
      }
    },

    activeInputIndex(newVal, oldVal) {
      const _value = this.value || []
      const minDate = !this.singleValue ? this.minDate : _value[0] && new Date(_value[0])
      const maxDate = !this.singleValue ? this.maxDate : _value[1] && new Date(_value[1])

      if (maxDate && !minDate) {
        this.rangeState.selecting = newVal === 0
        this.markRange(maxDate)
      }
      if (minDate && !maxDate) {
        this.rangeState.selecting = newVal === 1
        this.markRange(minDate)
      }
    }
  },

  data() {
    return {
      tableRows: [[], [], [], [], [], []],
      popperOption: {},
      popperContent: null
    }
  },
  methods: {
    startEndDateRows() {
      const showWeekNumber = this.showWeekNumber
      const startCellIndex = showWeekNumber ? 1 : 0
      const startCells = this.rows[0]
      const startCell = startCells[startCellIndex]
      const startDate = this.getRowDate({
        cellIndex: startCellIndex,
        rowIndex: 0,
        className: startCell.type
      })
      const endCellIndex = showWeekNumber ? 7 : 6
      const endCells = this.rows[5]
      const endCell = endCells[endCellIndex]
      const endDate = this.getRowDate({
        cellIndex: endCellIndex,
        rowIndex: 5,
        className: endCell.type
      })
      return [moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD')]
    },
    // 设置气泡组件组件属性与内容
    setHeaderPopoverAttrs(reference, params) {
      this.$refs.popoverRef.referenceElm = reference
      this.$refs.popoverRef.createPopper()
      this.$refs.popoverRef.showPopper = true
      this.$nextTick(() => {
        this.$refs.popoverRef.$refs.popper.style.zIndex = PopupManager.nextZIndex()
      })
    },
    handleDatePopoverEvent(event, params) {
      this.popoverTimer = setTimeout(() => {
        if (this.$props.cellPopperContent) {
          const h = this.$createElement
          const isFunction = typeof this.$props.cellPopperContent === 'function'
          const content = isFunction ? this.$props.cellPopperContent(h, params) : this.$props.cellPopperContent
          this.popperContent = content
          if (content && content !== 0) {
            this.$nextTick(() => {
              this.setHeaderPopoverAttrs(event.target, params)
            })
          }
        }
      }, 300)
    },
    handleMouseEnter(event, cell) {
      this.hiddDatePopper()
      const newDate = this.getCurrentTargetDate(event, false)
      const params = {
        newDate,
        event,
        cell
      }
      clearTimeout(this.popoverTimer)
      this.handleDatePopoverEvent(event, params)
      this.$parent.handleDateMouseEnter && this.$parent.handleDateMouseEnter(params)
    },
    hiddDatePopper() {
      if (this.$refs.popoverRef) {
        this.$refs.popoverRef.showPopper = false
      }
    },
    handleMouseLeave(event, cell) {
      clearTimeout(this.popoverTimer)
      this.hiddDatePopper()
      const newDate = this.getCurrentTargetDate(event, false)
      this.$parent.handleDateMouseLeave &&
        this.$parent.handleDateMouseLeave({
          newDate,
          event,
          cell
        })
    },
    cellMatchesDate(cell, date) {
      const value = new Date(date)
      return (
        this.year === value.getFullYear() && this.month === value.getMonth() && Number(cell.text) === value.getDate()
      )
    },

    getCellClasses(cell) {
      const selectionMode = this.selectionMode
      const defaultValue = this.defaultValue
        ? Array.isArray(this.defaultValue)
          ? this.defaultValue
          : [this.defaultValue]
        : []

      let classes = []
      if ((cell.type === 'normal' || cell.type === 'today') && !cell.disabled) {
        classes.push('available')
        if (cell.type === 'today') {
          classes.push('today')
        }
      } else {
        classes.push(cell.type)
      }

      if (cell.type === 'normal' && defaultValue.some(date => this.cellMatchesDate(cell, date))) {
        classes.push('default')
      }

      if (
        selectionMode === 'day' &&
        (cell.type === 'normal' || cell.type === 'today') &&
        this.cellMatchesDate(cell, this.value)
      ) {
        classes.push('current')
      }

      if (
        cell.inRange &&
        (cell.type === 'normal' ||
          cell.type === 'today' ||
          this.selectionMode === 'week' ||
          cell.type === 'prev-month' ||
          cell.type === 'next-month')
      ) {
        classes.push('in-range')

        if (cell.start) {
          classes.push('start-date')
        }

        if (cell.end) {
          classes.push('end-date')
        }
      } else {
        // 单边时,只要有值，就加对应的样式
        if (this.$props.singleValue) {
          if (cell.start) {
            classes.push('start-date')
          }

          if (cell.end) {
            classes.push('end-date')
          }
        }
      }

      if (cell.disabled) {
        classes.push('disabled')
      }

      if (cell.selected) {
        classes.push('selected')
      }
      if (cell.isTodayWeek) {
        classes.push('isTodayWeek')
      }

      if (cell.customClass) {
        classes.push(cell.customClass)
      }

      return classes.join(' ')
    },

    getDateOfCell(row, column) {
      const offsetFromStart = row * 7 + (column - (this.showWeekNumber ? 1 : 0)) - this.offsetDay
      return nextDate(this.startDate, offsetFromStart)
    },

    isWeekActive(cell) {
      if (this.selectionMode !== 'week') return false
      const newDate = new Date(this.year, this.month, 1)
      const year = newDate.getFullYear()
      const month = newDate.getMonth()

      if (cell.type === 'prev-month') {
        newDate.setMonth(month === 0 ? 11 : month - 1)
        newDate.setFullYear(month === 0 ? year - 1 : year)
      }

      if (cell.type === 'next-month') {
        newDate.setMonth(month === 11 ? 0 : month + 1)
        newDate.setFullYear(month === 11 ? year + 1 : year)
      }

      newDate.setDate(parseInt(cell.text, 10))
      if (isDate(this.value)) {
        const dayOffset = ((this.value.getDay() - this.firstDayOfWeek + 7) % 7) - 1
        const weekDate = prevDate(this.value, dayOffset)
        return weekDate.getTime() === newDate.getTime()
        // const valueYear = this.value.getFullYear()
        // return year === valueYear && getWeekNumber(newDate) === getWeekNumber(this.value)
      }
      // 周范围处理
      if (isDate(this.minDate)) {
        const lDayOffset = ((this.minDate.getDay() - this.firstDayOfWeek + 7) % 7) - 1
        const weekDate = prevDate(this.minDate, lDayOffset)
        const isCheck = weekDate.getTime() === newDate.getTime()
        if (isCheck) return true
      }
      if (isDate(this.maxDate)) {
        const rDayOffset = ((this.maxDate.getDay() - this.firstDayOfWeek + 7) % 7) - 1
        const weekDate = prevDate(this.maxDate, rDayOffset)
        const isCheck = weekDate.getTime() === newDate.getTime()
        if (isCheck) return true
      }
      return false
      // const valueYear = isDate(this.value) ? this.value.getFullYear() : null
      // return year === valueYear && getWeekNumber(newDate) === getWeekNumber(this.value)
    },

    markRange(maxDate) {
      const hoverDate = this.rangeState.hoverDate
      const startDate = this.startDate
      const rows = this.rows
      const activeInputIndex = this.activeInputIndex
      let minDate = this.minDate

      if (!maxDate) {
        maxDate = this.maxDate
      }

      if (this.maxDate && !this.minDate) {
        minDate = maxDate
        maxDate = this.maxDate
      }

      for (let i = 0, k = rows.length; i < k; i++) {
        const row = rows[i]
        for (let j = 0, l = row.length; j < l; j++) {
          if (this.showWeekNumber && j === 0) continue

          const cell = row[j]
          const index = i * 7 + j + (this.showWeekNumber ? -1 : 0)
          const time = nextDate(startDate, index - this.offsetDay).getTime()

          if (maxDate && maxDate < minDate) {
            cell.inRange = minDate && time >= clearHours(maxDate) && time <= clearHours(minDate)
            cell.start = maxDate && time === clearHours(maxDate.getTime())
            cell.end = minDate && time === clearHours(minDate.getTime())
          } else {
            cell.start = minDate && time === clearHours(minDate.getTime())
            cell.end = maxDate && time === clearHours(maxDate.getTime())
            cell.inRange = (minDate && time >= clearHours(minDate) && time <= clearHours(maxDate)) || cell.start || cell.end
          }

          if (this.minDate && this.maxDate && hoverDate) {
            if (activeInputIndex === 0) {
              if (hoverDate < minDate) {
                cell.inRange = time >= clearHours(hoverDate) && time <= clearHours(maxDate)
              }
              if (hoverDate > maxDate) {
                cell.inRange = cell.start || cell.end
              }
              if (hoverDate > minDate && hoverDate < maxDate) {
                cell.inRange = (time >= clearHours(hoverDate) && time <= clearHours(maxDate)) || cell.start
              }
            }
            if (activeInputIndex === 1) {
              if (hoverDate > maxDate) {
                cell.inRange = time >= clearHours(minDate) && time <= clearHours(hoverDate)
              }
              if (hoverDate > minDate && hoverDate < maxDate) {
                cell.inRange = (time >= clearHours(minDate) && time <= clearHours(hoverDate)) || cell.end
              }
            }
          }
        }
      }
    },
    seStartEndCellClasses(date, type) {
      const startDate = this.startDate
      const rows = this.rows
      for (let i = 0, k = rows.length; i < k; i++) {
        const row = rows[i]
        for (let j = 0, l = row.length; j < l; j++) {
          if (this.showWeekNumber && j === 0) continue
          const cell = row[j]
          const index = i * 7 + j + (this.showWeekNumber ? -1 : 0)
          const time = nextDate(startDate, index - this.offsetDay).getTime()
          if (type === 'min') {
            cell.start = date && time === clearHours(date.getTime())
          } else {
            cell.end = date && time === clearHours(date.getTime())
          }
        }
      }
    },

    handleMouseMove(event) {
      if (this.isWeekRange) return

      let target = event.target
      if (target.tagName === 'SPAN') {
        target = target.parentNode.parentNode
      }
      if (target.tagName === 'EM') {
        target = target.parentNode.parentNode.parentNode
      }

      if (target.tagName === 'DIV') {
        target = target.parentNode
      }
      if (target.tagName !== 'TD') return

      const column = target.cellIndex
      const row = target.parentNode.rowIndex - 1
      const { row: oldRow, column: oldColumn } = this.rangeState

      if (this.rows[row][column].disabled) return

      if (!this.rangeState.selecting) {
        this.rangeState.hoverDate = this.getDateOfCell(row, column)
        return
      }

      if (oldRow !== row || oldColumn !== column) {
        this.rangeState.row = row
        this.rangeState.column = column
        this.rangeState.endDate = this.minDate && this.maxDate ? null : this.getDateOfCell(row, column)
        this.rangeState.hoverDate = this.getDateOfCell(row, column)
      }

      this.$emit('changerange', {
        minDate: this.minDate,
        maxDate: this.maxDate,
        rangeState: this.rangeState
      })
    },
    handleTableMouseLeave () {
      this.rangeState.hoverDate = null
    },
    getCurrentTargetDate(event, disabled = true) {
      let target = event.target
      if (target.tagName === 'SPAN') {
        target = target.parentNode.parentNode
      }
      if (target.tagName === 'EM') {
        target = target.parentNode.parentNode.parentNode
      }
      if (target.tagName === 'DIV') {
        target = target.parentNode
      }

      if (target.tagName !== 'TD') return
      if (disabled && (hasClass(target, 'disabled') || hasClass(target, 'week'))) return

      const selectionMode = this.selectionMode

      if (selectionMode === 'week') {
        target = this.showWeekNumber ? target.parentNode.cells[1] : target.parentNode.cells[0]
      }
      const cellIndex = target.cellIndex
      const rowIndex = target.parentNode.rowIndex - 1
      const className = target.className
      return this.getRowDate({
        cellIndex,
        rowIndex,
        className
      })
    },
    getRowDate({ cellIndex, rowIndex, className }) {
      let year = Number(this.year)
      let month = Number(this.month)

      const cell = this.rows[rowIndex][cellIndex]
      const text = cell.text

      const newDate = new Date(year, month, 1)

      if (className.indexOf('prev') !== -1) {
        if (month === 0) {
          year = year - 1
          month = 11
        } else {
          month = month - 1
        }
        newDate.setFullYear(year)
        newDate.setMonth(month)
      } else if (className.indexOf('next') !== -1) {
        if (month === 11) {
          year = year + 1
          month = 0
        } else {
          month = month + 1
        }
        newDate.setFullYear(year)
        newDate.setMonth(month)
      }

      newDate.setDate(parseInt(text, 10))
      return newDate
    },
    handleClick(event) {
      let target = event.target
      if (target.tagName === 'SPAN') {
        target = target.parentNode.parentNode
      }
      if (target.tagName === 'EM') {
        target = target.parentNode.parentNode.parentNode
      }

      if (target.tagName === 'DIV') {
        target = target.parentNode
      }

      if (target.tagName !== 'TD') return
      if (hasClass(target, 'disabled') || hasClass(target, 'week')) return
      const row = target.parentNode.rowIndex - 1
      const column = this.selectionMode === 'week' ? 1 : target.cellIndex
      const cell = this.rows[row][column]

      if (cell.disabled || cell.type === 'week') return
      const newDate = this.getCurrentTargetDate(event)
      const selectionMode = this.selectionMode
      if (this.selectionMode === 'range') {
        const activeInputIndex = this.activeInputIndex
        const date = new Date(newDate.getTime())
        let _maxDate = this.maxDate
        let _value = this.value || []
        // 单边值只有minDate
        if (this.singleValue && activeInputIndex === 1 && !_value[0] && _value[1]) {
          _maxDate = _value[1]
        }
        if (this.minDate && _maxDate) {
          let minDate = date
          let maxDate = null
          let isPick = false
          let selecting = true
          let isColse = false
          // 已选值后，二次选中时间允许只改变单个值
          if (activeInputIndex === 1 && date >= this.minDate) {
            minDate = this.minDate
            maxDate = date
            selecting = false
            isColse = true
          }

          if (activeInputIndex === 0 && date <= this.maxDate && !this.rangeState.isPick) {
            minDate = date
            maxDate = this.maxDate
            isPick = true
            this.rangeState.isPick = true
          }

          // // 时间区间需要点【确认】操作后才最终修改时间
          // if (this.showTime) {
          //   if (activeInputIndex === 1) {
          //     minDate = this.minDate
          //     maxDate = date
          //     selecting = false
          //     isColse = true
          //   }
          //   if (activeInputIndex === 0) {
          //     minDate = date
          //     maxDate = this.maxDate
          //     selecting = false
          //   }
          // }

          this.rangeState.selecting = selecting
          this.$emit('pick', { minDate, maxDate, isDateRangePick: isPick }, isColse)
          if (isPick) {
            this.markRange(this.minDate)
            this.$nextTick(() => {
              this.handleMouseMove(event)
            })
          }
        } else if (this.minDate && !_maxDate) {
          // 未点击确认前，允许修改
          if (this.showTime) {
            let minDate = date
            let maxDate = this.maxDate
            if (activeInputIndex === 1) {
              minDate = this.minDate
              maxDate = date
            }
            this.$emit('pick', { minDate, maxDate })
            return
          }

          // 用户再次重新主动聚焦（未选结束时间）
          if (activeInputIndex === 0) {
            const minDate = new Date(newDate.getTime())
            this.rangeState.selecting = true
            this.$emit('pick', { minDate, maxDate: null })
            return
          }

          if (newDate >= this.minDate) {
            const maxDate = new Date(newDate.getTime())
            this.rangeState.selecting = false

            this.$emit('pick', {
              minDate: this.minDate,
              maxDate
            })
          } else {
            const minDate = new Date(newDate.getTime())
            this.rangeState.selecting = false

            this.$emit('pick', { minDate, maxDate: this.minDate })
          }
        } else if (!this.minDate) {
          let minDate = date
          let maxDate = this.maxDate
          // let isColse = false
          let selecting = !this.showTime
          // 用户再次重新主动聚焦（未选开始时间）
          if (!this.singleValue && activeInputIndex === 1) {
            minDate = this.minDate
            maxDate = date
          }

          this.$emit('pick', { minDate, maxDate }, maxDate && minDate)
          this.rangeState.selecting = maxDate && minDate ? false : selecting
          this.markRange(this.minDate)
        }
      } else if (selectionMode === 'day') {
        this.$emit('pick', newDate)
      } else if (selectionMode === 'week') {
        const weekNumber = getWeekNumber(newDate)

        const value = newDate.getFullYear() + 'w' + weekNumber
        this.$emit('pick', {
          year: newDate.getFullYear(),
          week: weekNumber,
          value: value,
          date: newDate
        })
      } else if (selectionMode === 'dates') {
        const value = this.value || []
        const newValue = cell.selected
          ? removeFromArray(value, date => moment(date).isSame(moment(newDate), 'day'))
          : [...value, newDate]
        this.$emit('pick', newValue)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/public/components/kye-date-picker3/src/basic/date-table.vue